import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../../../styles/teamsIntegration.css';
import { supabaseClient } from '../../../config/supabase';
import Button3D from '../../ui/Button3D';
import { useSaveToast } from '../../ui/SaveToast';
import { parseCallbackParams } from '../../../lib/integrations/teamsOAuth';
import { PillGroup } from './TeamsControls';
import TeamsTextMirror from './TeamsTextMirror';
import { ALERT_OPTIONS } from '../../../lib/integrations/teamsSettings';

export const MIRROR_OPTIONS = Object.freeze([['picked', 'Only chats I pick'], ['client_chats', 'All my client chats'], ['everything', 'Everything']]);
export { ALERT_OPTIONS } from '../../../lib/integrations/teamsSettings';

const STATUS_WORDS = {
    connected: 'Connected',
    pending: 'Not connected yet',
    disconnected: 'Disconnected',
    refused: 'That account was refused',
    error: 'Needs attention',
};

async function invoke(client, body) {
    const { data, error } = await client.functions.invoke('teams-connection', { body });
    if (error) {
        let message = error.message;
        try { const detail = await error.context?.json?.(); if (detail?.error) message = detail.error; } catch { /* keep the generic message */ }
        throw new Error(message || 'Teams could not be reached.');
    }
    return data;
}

// Teams PR A (board 3): Settings > Communications > Teams mirror, inside the Settings popup. Rendered only while Teams is on for
// this person (the Settings tile is hidden otherwise, and every call below is refused by the database / Edge Function anyway).
// In PR A nothing is mirrored yet: the chat list is a placeholder until PR B.
export default function TeamsMirrorSettings({ userId, client = supabaseClient, locationSearch = typeof window !== 'undefined' ? window.location.search : '', onReturnHandled }) {
    const toast = useSaveToast();
    const [connection, setConnection] = useState(undefined);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const handledReturn = useRef(false);

    const load = useCallback(async () => {
        const { data, error: loadError } = await client.rpc('teams_my_connection');
        if (loadError) { setError(loadError.message || 'Your Teams connection could not be loaded.'); setConnection(null); return; }
        setConnection(data || null);
    }, [client]);

    // Microsoft sends the person back here with ?code&state. Finish once, then clean the address bar.
    useEffect(() => {
        const callback = parseCallbackParams(locationSearch);
        if (!callback || handledReturn.current) { load(); return; }
        handledReturn.current = true;
        (async () => {
            setBusy(true);
            try {
                if (callback.error) throw new Error(callback.errorDescription || 'Microsoft did not finish the sign-in.');
                const result = await invoke(client, { action: 'complete', code: callback.code, state: callback.state });
                if (result?.status === 'refused') setError('That Microsoft account belongs to a different organization. Use your work account.');
                else toast.saved();
            } catch (failure) {
                setError(failure?.message || 'Teams could not be connected.');
            } finally {
                setBusy(false);
                if (onReturnHandled) onReturnHandled();
                else if (typeof window !== 'undefined') window.history.replaceState(window.history.state, '', window.location.pathname);
                load();
            }
        })();
    }, [client, load, locationSearch, onReturnHandled, toast]);

    const connect = async () => {
        setBusy(true); setError('');
        try {
            const result = await invoke(client, { action: 'start' });
            if (!result?.url) throw new Error('Teams sign-in is not ready yet.');
            window.location.assign(result.url);
        } catch (failure) {
            setError(failure?.message || 'Teams sign-in could not start.');
            setBusy(false);
        }
    };

    const disconnect = async () => {
        setBusy(true); setError('');
        const { error: failure } = await client.rpc('teams_disconnect');
        if (failure) setError(failure.message || 'Teams could not be disconnected.'); else toast.saved();
        setBusy(false);
        load();
    };

    const savePreference = async (column, value) => {
        setBusy(true); setError('');
        const { data, error: failure } = await client.from('teams_connections').update({ [column]: value }).eq('user_id', userId).select('mirror_scope, new_chat_alert');
        if (failure || !data || data.length === 0) {
            const message = failure?.message || 'That preference could not be saved.';
            setError(message); toast.failed(message);
        } else {
            setConnection((current) => ({ ...current, ...data[0] })); toast.saved();
        }
        setBusy(false);
    };

    if (connection === undefined) return <p>Loading…</p>;
    const connected = connection?.status === 'connected';
    const hasRow = Boolean(connection);

    return (
        <div className="ia-teams" data-testid="teams-mirror-settings">
            <div className="ia-teams__line ia-teams__notice" data-testid="teams-connection-status">
                <span className={`ia-teams__status-dot${connected ? ' ia-teams__status-dot--on' : ''}`} aria-hidden="true" />
                <span className="ia-teams__line-label">
                    {connected
                        ? <>Connected as <strong>{connection.m365_upn || connection.display_name || 'your work account'}</strong></>
                        : STATUS_WORDS[connection?.status] || 'Not connected'}
                    {connection?.last_error && <span className="ia-teams__hint block">{connection.last_error}</span>}
                </span>
                {connected
                    ? <Button3D onClick={disconnect} disabled={busy}>Disconnect</Button3D>
                    : <Button3D variant="go" onClick={connect} disabled={busy}>{busy ? 'Working…' : 'Connect Teams'}</Button3D>}
            </div>

            <div className="ia-teams__field">
                <strong>What to mirror</strong>
                <PillGroup options={MIRROR_OPTIONS} value={connection?.mirror_scope || 'picked'} disabled={busy || !hasRow}
                    onChange={(value) => savePreference('mirror_scope', value)} label="What to mirror" testId="teams-mirror-scope" />
                <span className="ia-teams__hint">Starts with nothing mirrored. Client chats go to Conversations; staff chats and channels go to Team chat.</span>
            </div>

            {connected && <TeamsTextMirror userId={userId} client={client} mode="settings" />}
            {!connected && <p className="ia-teams__notice" data-testid="teams-chat-list-placeholder">Nothing is mirrored yet. Connect Teams to pick chats and channels.</p>}

            <div className="ia-teams__field">
                <strong>New chats start at</strong>
                <PillGroup options={ALERT_OPTIONS} value={connection?.new_chat_alert || 'mentions'} disabled={busy || !hasRow}
                    onChange={(value) => savePreference('new_chat_alert', value)} label="New chats start at" testId="teams-new-chat-alert" />
            </div>
            {!hasRow && <span className="ia-teams__hint">Connect Teams first to choose these.</span>}
            {error && <p role="alert" className="ia-teams__error">{error}</p>}
            <span className="ia-teams__hint">Your company admin decides in Admin Center whether Teams mirroring is allowed at all.</span>
        </div>
    );
}
